import { Currency, Dtos, type UserId } from "@inrev/common";
import { type EntityScope, scopeMatches } from "../../../shared/scope";

type BondPreview = Pick<
	Dtos.SuretyBond.Preview.Get.Response,
	"status" | "type" | "amount" | "premium" | "underwriterUserId"
>;

export type BondMetricsInput = {
	bondPreviews: BondPreview[];
	scope: EntityScope;
	currentUserId: UserId;
};

export type BondMetrics = {
	open: number;
	openBids: number;
	openFinals: number;
	averageGwp: Currency;
	averageBondAmount: Currency;
};

export const computeBondMetrics = ({
	bondPreviews,
	scope,
	currentUserId,
}: BondMetricsInput): BondMetrics => {
	const scopedBonds = bondPreviews.filter((bond) =>
		scopeMatches(scope, bond.underwriterUserId, currentUserId),
	);

	const openBonds = scopedBonds.filter((bond) => bond.status === "open");
	const openBidBonds = openBonds.filter((bond) => bond.type === "bid");
	const openFinalBonds = openBonds.filter((bond) => bond.type === "final");
	const openNonBidBonds = openBonds.filter((bond) => bond.type !== "bid");

	const totalOpenNonBidPremium = Currency.sum(openNonBidBonds.map((bond) => bond.premium));
	const totalOpenNonBidExposure = Currency.sum(openNonBidBonds.map((bond) => bond.amount));

	return {
		open: openBonds.length,
		openBids: openBidBonds.length,
		openFinals: openFinalBonds.length,
		averageGwp:
			openNonBidBonds.length === 0
				? Currency.ZERO
				: Currency.round(totalOpenNonBidPremium / openNonBidBonds.length),
		averageBondAmount:
			openNonBidBonds.length === 0
				? Currency.ZERO
				: Currency.round(totalOpenNonBidExposure / openNonBidBonds.length),
	};
};
